import React, { useEffect, useMemo, useState, Children, Fragment, Component } from 'react';
import { motion } from 'framer-motion';
import { Search, MessageSquare, Video, ChevronDown, SearchX, LayoutGrid, List, Star, Sparkles, ArrowRight, Crown, ShieldCheck, Gem, Pin, Heart, PlusCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { SearchBar } from '../components/ui/SearchBar';
import { Rating } from '../components/ui/Rating';
import { Pagination } from '../components/ui/Pagination';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { AdSlot } from '../components/ads/AdSlot';
import { useAstrologerProfile } from '../contexts/AstrologerProfileContext';
import { useCart } from '../contexts/CartContext';
interface AstrologersCatalogPageProps {
  onNavigate: (page: string) => void;
}
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
// Badge icon component with tooltip
function StatusIcon({
  badge,
  size = 'md'
}: {
  badge: string | null;
  size?: 'sm' | 'md';
}) {
  if (!badge) return null;
  const sizeClasses = size === 'sm' ? 'w-5 h-5' : 'w-6 h-6';
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5';
  const tooltipText = badge === 'TOP' ? 'TOP — лучший рейтинг и отзывы' : badge === 'PRO' ? 'PRO — подтверждённый профессионал' : 'Рекомендуем — выбор редакции';
  const bgColor = badge === 'TOP' ? 'bg-amber-400' : badge === 'PRO' ? 'bg-indigo-500' : 'bg-emerald-500';
  const tooltipBg = badge === 'TOP' ? 'bg-amber-900' : badge === 'PRO' ? 'bg-indigo-900' : 'bg-emerald-900';
  const IconComponent = badge === 'TOP' ? Crown : badge === 'PRO' ? ShieldCheck : Gem;
  return <div className="relative group/badge cursor-pointer" data-id="element-21301">
      <div className={`${sizeClasses} rounded-full ${bgColor} flex items-center justify-center ring-2 ring-white shadow-sm`} data-id="element-21302">
        <IconComponent className={`${iconSize} text-white`} data-id="element-21303" />
      </div>
      <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1.5 ${tooltipBg} text-white text-[10px] font-medium rounded-lg whitespace-nowrap opacity-0 invisible group-hover/badge:opacity-100 group-hover/badge:visible transition-all duration-200 pointer-events-none z-50 shadow-lg`} data-id="element-21304">
        {tooltipText}
        <div className={`absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent ${badge === 'TOP' ? 'border-t-amber-900' : badge === 'PRO' ? 'border-t-indigo-900' : 'border-t-emerald-900'}`} data-id="element-21305" />
      </div>
    </div>;
}
// Pinned icon for avatar
function PinnedIcon({
  size = 'md'
}: {
  size?: 'sm' | 'md';
}) {
  const sizeClasses = size === 'sm' ? 'w-5 h-5' : 'w-6 h-6';
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5';
  return <div className="relative group/pin cursor-pointer" data-id="element-21306">
      <div className={`${sizeClasses} rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center ring-2 ring-white shadow-md`} data-id="element-21307">
        <Pin className={`${iconSize} text-white rotate-45`} data-id="element-21308" />
      </div>
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1.5 bg-amber-900 text-white text-[10px] font-medium rounded-lg whitespace-nowrap opacity-0 invisible group-hover/pin:opacity-100 group-hover/pin:visible transition-all duration-200 pointer-events-none z-50 shadow-lg" data-id="element-21309">
        Платный закреп — топ в рубрике
        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-amber-900" data-id="element-21310" />
      </div>
    </div>;
}
// Compact badge pill for card corners
function BadgePill({
  badge,
  pinned
}: {
  badge: string | null;
  pinned: boolean;
}) {
  const pills: {
    icon: React.ReactNode;
    text: string;
    className: string;
    tooltip: string;
  }[] = [];
  if (pinned) {
    pills.push({
      icon: <Pin className="w-2.5 h-2.5 rotate-45" data-id="element-21311" />,
      text: 'Закреп',
      className: 'bg-amber-100 text-amber-700 border-amber-200',
      tooltip: 'Платный закреп — топ-позиция в рубрике'
    });
  }
  if (badge === 'TOP') {
    pills.push({
      icon: <Crown className="w-2.5 h-2.5" data-id="element-21312" />,
      text: 'TOP',
      className: 'bg-amber-100 text-amber-700 border-amber-200',
      tooltip: 'TOP — лучший рейтинг и отзывы'
    });
  }
  if (badge === 'PRO') {
    pills.push({
      icon: <ShieldCheck className="w-2.5 h-2.5" data-id="element-21313" />,
      text: 'PRO',
      className: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      tooltip: 'PRO — подтверждённый профессионал'
    });
  }
  if (badge === 'Featured') {
    pills.push({
      icon: <Gem className="w-2.5 h-2.5" data-id="element-21314" />,
      text: 'Рек.',
      className: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      tooltip: 'Рекомендуем — выбор редакции'
    });
  }
  if (pills.length === 0) return null;
  return <div className="flex flex-col gap-0.5 items-end" data-id="element-21315">
      {pills.map((pill, i) => <div key={i} className="relative group/pill" data-id="element-21316">
          <span className={`inline-flex items-center gap-0.5 text-[8px] sm:text-[9px] font-bold px-1 sm:px-1.5 py-0.5 rounded-full border ${pill.className}`} data-id="element-21317">
            {pill.icon}
            {pill.text}
          </span>
          {/* Tooltip — hidden on touch devices, only desktop hover */}
          <div className="hidden sm:block absolute top-full right-0 mt-1 px-2 py-1 bg-slate-800 text-white text-[10px] font-medium rounded-md whitespace-nowrap opacity-0 invisible group-hover/pill:opacity-100 group-hover/pill:visible transition-all duration-150 pointer-events-none z-50 shadow-lg" data-id="element-21318">
            {pill.tooltip}
            <div className="absolute bottom-full right-2 border-4 border-transparent border-b-slate-800" data-id="element-21319" />
          </div>
        </div>)}
    </div>;
}
// Inline badge pills for list view (horizontal)
function InlineBadges({
  badge,
  pinned
}: {
  badge: string | null;
  pinned: boolean;
}) {
  const pills: {
    icon: React.ReactNode;
    text: string;
    className: string;
    tooltip: string;
  }[] = [];
  if (pinned) {
    pills.push({
      icon: <Pin className="w-2 h-2 sm:w-2.5 sm:h-2.5 rotate-45" data-id="element-21320" />,
      text: 'Закреп',
      className: 'bg-amber-100 text-amber-700',
      tooltip: 'Платный закреп — топ-позиция в рубрике'
    });
  }
  if (badge === 'TOP') {
    pills.push({
      icon: <Crown className="w-2 h-2 sm:w-2.5 sm:h-2.5" data-id="element-21321" />,
      text: 'TOP',
      className: 'bg-amber-100 text-amber-700',
      tooltip: 'TOP — лучший рейтинг и отзывы'
    });
  }
  if (badge === 'PRO') {
    pills.push({
      icon: <ShieldCheck className="w-2 h-2 sm:w-2.5 sm:h-2.5" data-id="element-21322" />,
      text: 'PRO',
      className: 'bg-indigo-100 text-indigo-700',
      tooltip: 'PRO — подтверждённый профессионал'
    });
  }
  if (badge === 'Featured') {
    pills.push({
      icon: <Gem className="w-2 h-2 sm:w-2.5 sm:h-2.5" data-id="element-21323" />,
      text: 'Рек.',
      className: 'bg-emerald-100 text-emerald-700',
      tooltip: 'Рекомендуем — выбор редакции'
    });
  }
  if (pills.length === 0) return null;
  return <>
      {pills.map((pill, i) => <div key={i} className="relative group/pill inline-flex" data-id="element-21324">
          <span className={`inline-flex items-center gap-0.5 text-[7px] sm:text-[8px] font-bold px-1 py-0 rounded-full whitespace-nowrap ${pill.className}`} data-id="element-21325">
            {pill.icon}
            {pill.text}
          </span>
          {/* Tooltip — hidden on touch/mobile */}
          <div className="hidden sm:block absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-1 bg-slate-800 text-white text-[10px] font-medium rounded-md whitespace-nowrap opacity-0 invisible group-hover/pill:opacity-100 group-hover/pill:visible transition-all duration-150 pointer-events-none z-50 shadow-lg" data-id="element-21326">
            {pill.tooltip}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-slate-800" data-id="element-21327" />
          </div>
        </div>)}
    </>;
}
const ALL_SPECIALTIES = ['Натальная карта', 'Соляр', 'Совместимость', 'Детская', 'Бизнес', 'Хорарная', 'Элективная', 'Медицинская', 'Кармическая', 'Финансовая', 'Астропсихология', 'Другое'];
const MOCK_ASTROLOGERS = Array.from({
  length: 32
}).map((_, i) => {
  const names = ['Елена Смирнова', 'Михаил Волков', 'Анна Коваль', 'Дмитрий Орлов', 'София Романова', 'Александр Ким', 'Виктория Ларина', 'Павел Глоба', 'Мария Вей', 'Константин Левин'];
  const numSpecs = i % 3 + 1;
  const specialties: string[] = [];
  for (let j = 0; j < numSpecs; j++) {
    specialties.push(ALL_SPECIALTIES[(i + j) % ALL_SPECIALTIES.length]);
  }
  const formats: string[] = [];
  if (i % 2 === 0 || i % 3 === 0) formats.push('video');
  if (i % 2 !== 0 || i % 4 === 0) formats.push('chat');
  // Paid pinning: some astrologers are pinned in specific specialties
  const pinnedIn: string[] = [];
  if (i === 0) pinnedIn.push('Натальная карта'); // Елена pinned in Натальная карта
  if (i === 1) pinnedIn.push('Соляр'); // Михаил pinned in Соляр
  if (i === 2) pinnedIn.push('Совместимость'); // Анна pinned in Совместимость
  if (i === 5) pinnedIn.push('Бизнес'); // Александр pinned in Бизнес
  if (i === 7) pinnedIn.push('Натальная карта', 'Соляр'); // Павел pinned in 2 categories
  if (i === 10) pinnedIn.push('Детская'); // pinned in Детская
  return {
    id: i + 1,
    name: names[i % names.length],
    specialties: [...new Set(specialties)],
    rating: +(3.5 + i % 16 * 0.1).toFixed(1),
    reviews: 10 + i * 17 % 200,
    price: 1500 + i * 500 % 8000,
    languages: ['RU', i % 4 === 0 ? 'EN' : null].filter(Boolean) as string[],
    formats,
    experience: 2 + i % 12,
    badge: i % 7 === 0 ? 'TOP' : i % 5 === 0 ? 'PRO' : i % 11 === 0 ? 'Featured' : null,
    pinnedIn,
    avatarColor: ['bg-indigo-100 text-indigo-700', 'bg-amber-100 text-amber-700', 'bg-emerald-100 text-emerald-700', 'bg-rose-100 text-rose-700', 'bg-blue-100 text-blue-700'][i % 5]
  };
});
const ITEMS_PER_PAGE = 9;
type SortOption = 'rating' | 'reviews' | 'price_asc' | 'price_desc';
export function AstrologersCatalogPage({
  onNavigate
}: AstrologersCatalogPageProps) {
  const {
    profile
  } = useAstrologerProfile();
  const {
    addItem
  } = useCart();
  // Merge saved astrologer profile into MOCK_ASTROLOGERS (first entry = logged-in astrologer)
  const astrologersList = useMemo(() => {
    const list = [...MOCK_ASTROLOGERS];
    // Parse price from profile services (take the minimum price)
    const prices = profile.services.map(s => {
      const num = parseInt(s.price.replace(/\D/g, ''), 10);
      return isNaN(num) ? 0 : num;
    });
    const minPrice = prices.length > 0 ? Math.min(...prices.filter(p => p > 0)) : list[0].price;
    // Map specializations to catalog format
    const specMap: Record<string, string> = {
      'Западная астрология': 'Натальная карта',
      'Натальная карта': 'Натальная карта',
      Прогностика: 'Соляр',
      Синастрия: 'Совместимость',
      'Ведическая астрология': 'Другое',
      'Хорарная астрология': 'Хорарная',
      'Элективная астрология': 'Элективная',
      'Кармическая астрология': 'Кармическая',
      'Медицинская астрология': 'Медицинская',
      'Финансовая астрология': 'Финансовая',
      'Детская астрология': 'Детская',
      Астропсихология: 'Астропсихология',
      'Бизнес-астрология': 'Бизнес'
    };
    const mappedSpecs = [...new Set(profile.specializations.map(s => specMap[s] || s))];
    // Build formats from consultation settings
    const formats: string[] = [];
    if (profile.consultFormats.video) formats.push('video');
    if (profile.consultFormats.chat) formats.push('chat');
    // Build languages
    const langs = profile.languages.split(',').map(l => l.trim()).map(l => {
      if (l.toLowerCase().includes('рус')) return 'RU';
      if (l.toLowerCase().includes('англ') || l.toLowerCase().includes('eng')) return 'EN';
      return l.substring(0, 2).toUpperCase();
    }).filter(Boolean);
    list[0] = {
      ...list[0],
      name: `${profile.firstName} ${profile.lastName}`,
      specialties: mappedSpecs.length > 0 ? mappedSpecs : list[0].specialties,
      price: minPrice || list[0].price,
      languages: langs.length > 0 ? langs : list[0].languages,
      formats: formats.length > 0 ? formats : list[0].formats,
      experience: parseInt(profile.experience, 10) || list[0].experience
    };
    return list;
  }, [profile]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortOption>('rating');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState({
    min: '',
    max: ''
  });
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [minRating, setMinRating] = useState<number | null>(null);
  const [onlyFeatured, setOnlyFeatured] = useState(false);
  const [showAllSpecs, setShowAllSpecs] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [favorites, setFavorites] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  // Simulate loading on mount and filter/page changes
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 600);
    return () => clearTimeout(timer);
  }, [currentPage, searchQuery, selectedSpecialties, priceRange, selectedFormats, minRating, onlyFeatured, sortBy]);
  const toggleFavorite = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    if (mq.matches) setViewMode('list');
    const handler = (e: MediaQueryListEvent) => setViewMode(e.matches ? 'list' : 'grid');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  useEffect(() => {
    if (!isSortOpen) return;
    const handler = () => setIsSortOpen(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [isSortOpen]);
  const toggleSpecialty = (spec: string) => {
    setSelectedSpecialties(prev => prev.includes(spec) ? prev.filter(s => s !== spec) : [...prev, spec]);
    setCurrentPage(1);
  };
  const toggleFormat = (format: string) => {
    setSelectedFormats(prev => prev.includes(format) ? prev.filter(f => f !== format) : [...prev, format]);
    setCurrentPage(1);
  };
  const resetFilters = () => {
    setSearchQuery('');
    setSelectedSpecialties([]);
    setPriceRange({
      min: '',
      max: ''
    });
    setSelectedFormats([]);
    setMinRating(null);
    setOnlyFeatured(false);
    setCurrentPage(1);
  };
  const filteredAndSortedAstrologers = useMemo(() => {
    let result = [...astrologersList];
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(a => a.name.toLowerCase().includes(query) || a.specialties.some(s => s.toLowerCase().includes(query)));
    }
    if (selectedSpecialties.length > 0) {
      result = result.filter(a => selectedSpecialties.some(spec => a.specialties.includes(spec)));
    }
    if (priceRange.min) result = result.filter(a => a.price >= parseInt(priceRange.min));
    if (priceRange.max) result = result.filter(a => a.price <= parseInt(priceRange.max));
    if (selectedFormats.length > 0) {
      result = result.filter(a => selectedFormats.some(format => a.formats.includes(format)));
    }
    if (minRating) result = result.filter(a => a.rating >= minRating);
    if (onlyFeatured) result = result.filter(a => a.badge !== null);
    // Sort: pinned astrologers first when specialty filter is active, then by selected sort
    result.sort((a, b) => {
      // Pinned priority: if specialty filter is active, pinned in that specialty goes first
      if (selectedSpecialties.length > 0) {
        const aIsPinned = a.pinnedIn.some(p => selectedSpecialties.includes(p));
        const bIsPinned = b.pinnedIn.some(p => selectedSpecialties.includes(p));
        if (aIsPinned && !bIsPinned) return -1;
        if (!aIsPinned && bIsPinned) return 1;
      }
      switch (sortBy) {
        case 'rating':
          return b.rating - a.rating;
        case 'reviews':
          return b.reviews - a.reviews;
        case 'price_asc':
          return a.price - b.price;
        case 'price_desc':
          return b.price - a.price;
        default:
          return 0;
      }
    });
    return result;
  }, [astrologersList, searchQuery, selectedSpecialties, priceRange, selectedFormats, minRating, onlyFeatured, sortBy]);
  // Check if astrologer is pinned in current filter context
  const isPinnedInContext = (astrologer: (typeof astrologersList)[0]) => {
    if (selectedSpecialties.length === 0) return false;
    return astrologer.pinnedIn.some(p => selectedSpecialties.includes(p));
  };
  const totalPages = Math.ceil(filteredAndSortedAstrologers.length / ITEMS_PER_PAGE);
  const currentAstrologers = filteredAndSortedAstrologers.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const sortOptions: {
    value: SortOption;
    label: string;
  }[] = [{
    value: 'rating',
    label: 'По рейтингу'
  }, {
    value: 'reviews',
    label: 'По отзывам'
  }, {
    value: 'price_asc',
    label: 'Сначала дешевле'
  }, {
    value: 'price_desc',
    label: 'Сначала дороже'
  }];
  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('');
  // Skeleton components
  const SkeletonPulse = ({
    className = ''
  }: {
    className?: string;
  }) => <div className={`bg-slate-200 rounded animate-pulse ${className}`} data-id="element-21328" />;
  const SkeletonCard = () => <div className="flex flex-col h-full rounded-xl border border-slate-200 bg-white p-4" data-id="element-21329">
      {/* Avatar */}
      <div className="flex justify-center mb-3" data-id="element-21330">
        <SkeletonPulse className="w-14 h-14 rounded-full" data-id="element-21331" />
      </div>
      {/* Name */}
      <div className="flex flex-col items-center gap-1.5 mb-2" data-id="element-21332">
        <SkeletonPulse className="h-4 w-24" data-id="element-21333" />
        <SkeletonPulse className="h-3 w-20" data-id="element-21334" />
      </div>
      {/* Specialties */}
      <div className="flex justify-center gap-1 mb-2" data-id="element-21335">
        <SkeletonPulse className="h-4 w-16 rounded-full" data-id="element-21336" />
        <SkeletonPulse className="h-4 w-14 rounded-full" data-id="element-21337" />
      </div>
      {/* Info row */}
      <div className="flex justify-between items-center mb-3 mt-auto px-1" data-id="element-21338">
        <SkeletonPulse className="h-3 w-16" data-id="element-21339" />
        <SkeletonPulse className="h-3 w-10" data-id="element-21340" />
      </div>
      {/* Divider + buttons */}
      <div className="pt-3 border-t border-slate-100" data-id="element-21341">
        <div className="flex justify-between mb-2" data-id="element-21342">
          <SkeletonPulse className="h-3 w-20" data-id="element-21343" />
          <SkeletonPulse className="h-4 w-16" data-id="element-21344" />
        </div>
        <div className="flex gap-1.5" data-id="element-21345">
          <SkeletonPulse className="h-8 w-8 rounded-lg" data-id="element-21346" />
          <SkeletonPulse className="h-8 flex-1 rounded-lg" data-id="element-21347" />
          <SkeletonPulse className="h-8 flex-1 rounded-lg" data-id="element-21348" />
        </div>
      </div>
    </div>;
  const SkeletonRow = () => <div className="hidden lg:grid lg:grid-cols-12 gap-2 items-center px-4 py-3 border border-slate-100 rounded-lg mb-2 bg-white" data-id="element-21349">
      <div className="col-span-5 flex items-center gap-2.5" data-id="element-21350">
        <SkeletonPulse className="w-4 h-4 rounded-full" data-id="element-21351" />
        <SkeletonPulse className="w-9 h-9 rounded-full" data-id="element-21352" />
        <div className="flex flex-col gap-1.5" data-id="element-21353">
          <SkeletonPulse className="h-4 w-28" data-id="element-21354" />
          <SkeletonPulse className="h-3 w-20" data-id="element-21355" />
        </div>
      </div>
      <div className="col-span-3 flex gap-1" data-id="element-21356">
        <SkeletonPulse className="h-4 w-16 rounded" data-id="element-21357" />
        <SkeletonPulse className="h-4 w-14 rounded" data-id="element-21358" />
      </div>
      <div className="col-span-1" data-id="element-21359">
        <SkeletonPulse className="h-4 w-8" data-id="element-21360" />
      </div>
      <div className="col-span-3 flex justify-end gap-2" data-id="element-21361">
        <SkeletonPulse className="h-4 w-16" data-id="element-21362" />
        <SkeletonPulse className="h-8 w-20 rounded-lg" data-id="element-21363" />
      </div>
    </div>;
  const SkeletonMobileRow = () => <div className="lg:hidden flex items-start gap-2.5 px-3 py-3 border border-slate-100 rounded-lg mb-2 bg-white" data-id="element-21364">
      <SkeletonPulse className="w-10 h-10 rounded-full flex-shrink-0" data-id="element-21365" />
      <div className="flex-grow flex flex-col gap-1.5" data-id="element-21366">
        <SkeletonPulse className="h-4 w-28" data-id="element-21367" />
        <SkeletonPulse className="h-3 w-24" data-id="element-21368" />
        <SkeletonPulse className="h-3 w-32" data-id="element-21369" />
      </div>
      <div className="flex flex-col items-end gap-1.5 flex-shrink-0" data-id="element-21370">
        <SkeletonPulse className="h-4 w-14" data-id="element-21371" />
        <SkeletonPulse className="h-7 w-16 rounded-lg" data-id="element-21372" />
      </div>
    </div>;
  // Card styling based on badge + pinned status
  const getCardStyle = (badge: string | null, pinned: boolean) => {
    if (pinned) {
      return {
        grid: 'border-amber-300 ring-1 ring-amber-200 bg-gradient-to-br from-amber-50/60 to-white',
        list: 'bg-amber-50/50 border-amber-200 hover:border-amber-300',
        mobile: 'bg-amber-50/50 border-amber-200',
        divider: 'border-amber-200/60'
      };
    }
    switch (badge) {
      case 'TOP':
        return {
          grid: 'border-amber-200 bg-gradient-to-br from-amber-50/40 via-white to-amber-50/20 shadow-amber-100/50',
          list: 'bg-amber-50/30 border-amber-100 hover:border-amber-200',
          mobile: 'bg-amber-50/30 border-amber-100',
          divider: 'border-amber-100'
        };
      case 'PRO':
        return {
          grid: 'border-indigo-200 bg-gradient-to-br from-indigo-50/40 via-white to-indigo-50/20 shadow-indigo-100/50',
          list: 'bg-indigo-50/30 border-indigo-100 hover:border-indigo-200',
          mobile: 'bg-indigo-50/30 border-indigo-100',
          divider: 'border-indigo-100'
        };
      case 'Featured':
        return {
          grid: 'border-emerald-200 bg-gradient-to-br from-emerald-50/40 via-white to-emerald-50/20 shadow-emerald-100/50',
          list: 'bg-emerald-50/30 border-emerald-100 hover:border-emerald-200',
          mobile: 'bg-emerald-50/30 border-emerald-100',
          divider: 'border-emerald-100'
        };
      default:
        return {
          grid: 'border-slate-200 bg-white',
          list: 'bg-white border-slate-100 hover:border-slate-200',
          mobile: 'bg-white border-slate-100',
          divider: 'border-slate-100'
        };
    }
  };
  // Badge label for card top
  const getBadgeLabel = (badge: string | null) => {
    switch (badge) {
      case 'TOP':
        return {
          text: 'TOP Астролог',
          color: 'text-amber-600 bg-amber-100/80'
        };
      case 'PRO':
        return {
          text: 'PRO',
          color: 'text-indigo-600 bg-indigo-100/80'
        };
      case 'Featured':
        return {
          text: 'Рекомендуем',
          color: 'text-emerald-600 bg-emerald-100/80'
        };
      default:
        return null;
    }
  };
  return <div className="w-full bg-slate-50 min-h-screen pb-24" data-id="element-21373">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 pt-12 pb-12" data-id="element-21374">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21375">
          <div className="flex flex-col gap-6" data-id="element-21376">
            {/* Title — full width */}
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-3xl md:text-4xl font-bold text-slate-900" data-id="element-21377">
              Каталог астрологов
            </motion.h1>

            {/* Description + Search (left) | CTA card (right, full height) */}
            <div className="flex flex-col lg:flex-row lg:items-stretch gap-5" data-id="element-21378">
              <div className="flex-1 flex flex-col gap-5" data-id="element-21379">
                <motion.p initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.1
              }} className="text-lg text-slate-600 leading-relaxed max-w-2xl" data-id="element-21380">
                  Проверенные эксперты с высоким рейтингом — найдите своего
                  астролога для консультации или разбора.
                </motion.p>
                <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.2
              }} data-id="element-21381">
                  <SearchBar placeholder="Поиск по имени или специализации..." className="max-w-2xl" value={searchQuery} onChange={e => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }} onSearch={q => {
                  setSearchQuery(q);
                  setCurrentPage(1);
                }} data-id="element-21382" />
                </motion.div>
              </div>

              {/* CTA — stretches full height of desc+search */}
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.3
            }} className="flex-shrink-0 hidden lg:flex" data-id="element-21383">
                <div className="relative overflow-hidden bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl shadow-lg hover:shadow-xl transition-all cursor-pointer group flex items-center" onClick={() => onNavigate('for-astrologers')} data-id="element-21384">
                  <div className="absolute -top-8 -right-8 w-28 h-28 bg-white/10 rounded-full" data-id="element-21385" />
                  <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-white/5 rounded-full" data-id="element-21386" />

                  <div className="relative z-10 px-6 py-5 flex items-center gap-5" data-id="element-21387">
                    <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0" data-id="element-21388">
                      <PlusCircle className="w-5 h-5 text-white" data-id="element-21389" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-21390">
                      <h3 className="text-sm font-bold text-white mb-0.5" data-id="element-21391">
                        Вы астролог?
                      </h3>
                      <p className="text-xs text-amber-100 leading-snug" data-id="element-21392">
                        Добавьте себя в каталог
                      </p>
                    </div>
                    <button onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onNavigate('for-astrologers');
                  }} className="flex items-center gap-1.5 px-5 py-2.5 bg-white text-amber-700 rounded-xl text-sm font-bold hover:bg-amber-50 transition-colors whitespace-nowrap flex-shrink-0" data-id="element-21393">
                      Стать экспертом
                      <ArrowRight className="w-4 h-4" data-id="element-21394" />
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8" data-id="element-21395">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-21396">
          {/* Sidebar Filters */}
          <div className="w-full lg:w-64 flex-shrink-0" data-id="element-21397">
            <div className="sticky top-24 space-y-6" data-id="element-21398">
              <Card padding="md" data-id="element-21399">
                <div className="flex items-center justify-between mb-2 lg:mb-6" data-id="element-21400">
                  <button className="font-bold text-slate-900 flex items-center lg:cursor-default focus:outline-none" onClick={() => setShowMobileFilters(!showMobileFilters)} data-id="element-21401">
                    <Search className="w-4 h-4 mr-2" data-id="element-21402" /> Фильтры
                    <ChevronDown className={`w-4 h-4 ml-2 lg:hidden transition-transform ${showMobileFilters ? 'rotate-180' : ''}`} data-id="element-21403" />
                  </button>
                  <button onClick={resetFilters} className={`text-sm text-indigo-600 hover:text-indigo-700 hover:underline ${showMobileFilters ? '' : 'hidden lg:inline'}`} data-id="element-21404">
                    Сбросить
                  </button>
                </div>
                <div className={`space-y-6 ${showMobileFilters ? '' : 'hidden lg:block'}`} data-id="element-21405">
                  <div data-id="element-21406">
                    <h3 className="text-sm font-medium text-slate-900 mb-3" data-id="element-21407">
                      Специализация
                    </h3>
                    <div className="space-y-2" data-id="element-21408">
                      {(showAllSpecs ? ALL_SPECIALTIES : ALL_SPECIALTIES.slice(0, 5)).map(spec => <label key={spec} className="flex items-center cursor-pointer group" data-id="element-21409">
                          <input type="checkbox" checked={selectedSpecialties.includes(spec)} onChange={() => toggleSpecialty(spec)} className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4" data-id="element-21410" />
                          <span className="ml-2 text-sm text-slate-600 group-hover:text-slate-900 transition-colors" data-id="element-21411">
                            {spec}
                          </span>
                        </label>)}
                    </div>
                    {ALL_SPECIALTIES.length > 5 && <button onClick={() => setShowAllSpecs(!showAllSpecs)} className="mt-2 text-xs text-indigo-600 hover:text-indigo-700 hover:underline flex items-center gap-1" data-id="element-21412">
                        {showAllSpecs ? 'Свернуть' : `Ещё ${ALL_SPECIALTIES.length - 5}`}
                        <ChevronDown className={`w-3 h-3 transition-transform ${showAllSpecs ? 'rotate-180' : ''}`} data-id="element-21413" />
                      </button>}
                  </div>
                  <div data-id="element-21414">
                    <h3 className="text-sm font-medium text-slate-900 mb-3" data-id="element-21415">
                      Цена консультации (₽)
                    </h3>
                    <div className="flex items-center space-x-2" data-id="element-21416">
                      <Input placeholder="От" type="number" className="w-full mb-0" value={priceRange.min} onChange={e => {
                      setPriceRange(prev => ({
                        ...prev,
                        min: e.target.value
                      }));
                      setCurrentPage(1);
                    }} data-id="element-21417" />
                      <span className="text-slate-400" data-id="element-21418">-</span>
                      <Input placeholder="До" type="number" className="w-full mb-0" value={priceRange.max} onChange={e => {
                      setPriceRange(prev => ({
                        ...prev,
                        max: e.target.value
                      }));
                      setCurrentPage(1);
                    }} data-id="element-21419" />
                    </div>
                  </div>
                  <div data-id="element-21420">
                    <h3 className="text-sm font-medium text-slate-900 mb-3" data-id="element-21421">
                      Формат
                    </h3>
                    <div className="space-y-2" data-id="element-21422">
                      <label className="flex items-center cursor-pointer group" data-id="element-21423">
                        <input type="checkbox" checked={selectedFormats.includes('video')} onChange={() => toggleFormat('video')} className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4" data-id="element-21424" />
                        <span className="ml-2 text-sm text-slate-600 flex items-center group-hover:text-slate-900 transition-colors" data-id="element-21425">
                          <Video className="w-4 h-4 mr-1.5 text-slate-400 group-hover:text-indigo-500" data-id="element-21426" />{' '}
                          Видеозвонок
                        </span>
                      </label>
                      <label className="flex items-center cursor-pointer group" data-id="element-21427">
                        <input type="checkbox" checked={selectedFormats.includes('chat')} onChange={() => toggleFormat('chat')} className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4" data-id="element-21428" />
                        <span className="ml-2 text-sm text-slate-600 flex items-center group-hover:text-slate-900 transition-colors" data-id="element-21429">
                          <MessageSquare className="w-4 h-4 mr-1.5 text-slate-400 group-hover:text-indigo-500" data-id="element-21430" />{' '}
                          Текстовый чат
                        </span>
                      </label>
                    </div>
                  </div>
                  <div data-id="element-21431">
                    <h3 className="text-sm font-medium text-slate-900 mb-3" data-id="element-21432">
                      Рейтинг
                    </h3>
                    <div className="space-y-2" data-id="element-21433">
                      {[4.5, 4.0, 3.5].map(rating => <label key={rating} className="flex items-center cursor-pointer group" data-id="element-21434">
                          <input type="radio" name="rating" checked={minRating === rating} onChange={() => {
                        setMinRating(rating);
                        setCurrentPage(1);
                      }} className="border-slate-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4" data-id="element-21435" />
                          <span className="ml-2 text-sm text-slate-600 flex items-center group-hover:text-slate-900 transition-colors" data-id="element-21436">
                            <Rating value={rating} showCount={false} size="sm" className="mr-1.5" data-id="element-21437" />{' '}
                            и выше
                          </span>
                        </label>)}
                    </div>
                  </div>
                  <div className="pt-4 border-t border-slate-100" data-id="element-21438">
                    <label className="flex items-center justify-between cursor-pointer group" data-id="element-21439">
                      <span className="text-sm font-medium text-slate-900 group-hover:text-indigo-600 transition-colors" data-id="element-21440">
                        Только Featured
                      </span>
                      <div className="relative" data-id="element-21441">
                        <input type="checkbox" className="sr-only peer" checked={onlyFeatured} onChange={e => {
                        setOnlyFeatured(e.target.checked);
                        setCurrentPage(1);
                      }} data-id="element-21442" />
                        <div className="w-9 h-5 bg-slate-200 peer-focus:ring-2 peer-focus:ring-indigo-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600" data-id="element-21443"></div>
                      </div>
                    </label>
                  </div>
                </div>
              </Card>

              {/* Sidebar Ad */}
              <div className="hidden lg:block" data-id="element-21444">
                <AdSlot slotId="SLOT-02" onNavigate={onNavigate} data-id="element-21445" />
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1" data-id="element-21446">
            {/* Pinned Ad Cards — SLOT-04 */}
            <div className="mb-6" data-id="element-21447">
              <AdSlot slotId="SLOT-04" onNavigate={onNavigate} data-id="element-21448" />
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-21449">
              <span className="text-slate-600 font-medium" data-id="element-21450">
                Найдено: {filteredAndSortedAstrologers.length} астрологов
              </span>
              <div className="flex items-center gap-3" data-id="element-21451">
                <div className="flex items-center bg-white rounded-lg border border-slate-200 shadow-sm p-0.5" data-id="element-21452">
                  <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-indigo-100 text-indigo-700' : 'text-slate-400 hover:text-slate-600'}`} title="Карточки" data-id="element-21453">
                    <LayoutGrid className="w-4 h-4" data-id="element-21454" />
                  </button>
                  <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-indigo-100 text-indigo-700' : 'text-slate-400 hover:text-slate-600'}`} title="Таблица" data-id="element-21455">
                    <List className="w-4 h-4" data-id="element-21456" />
                  </button>
                </div>
                <div className="relative flex items-center bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm" data-id="element-21457">
                  <span className="text-sm text-slate-500 mr-2" data-id="element-21458">
                    Сортировка:
                  </span>
                  <button onClick={e => {
                  e.stopPropagation();
                  setIsSortOpen(!isSortOpen);
                }} className="text-sm font-medium text-slate-900 flex items-center hover:text-indigo-600 transition-colors" data-id="element-21459">
                    {sortOptions.find(o => o.value === sortBy)?.label}
                    <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} data-id="element-21460" />
                  </button>
                  {isSortOpen && <div className="absolute top-full right-0 mt-1 w-48 bg-white border border-slate-200 rounded-xl shadow-lg z-50 py-1" data-id="element-21461">
                      {sortOptions.map(option => <button key={option.value} className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors ${sortBy === option.value ? 'text-indigo-600 font-medium bg-indigo-50/50' : 'text-slate-700'}`} onClick={e => {
                    e.stopPropagation();
                    setSortBy(option.value);
                    setIsSortOpen(false);
                    setCurrentPage(1);
                  }} data-id="element-21462">
                          {option.label}
                        </button>)}
                    </div>}
                </div>
              </div>
            </div>

            {/* Empty State */}
            {isLoading ? <>
                {viewMode === 'grid' && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 mb-12" data-id="element-21463">
                    {Array.from({
                length: 6
              }).map((_, i) => <SkeletonCard key={i} data-id="element-21464" />)}
                  </div>}
                {viewMode === 'list' && <div className="mb-12" data-id="element-21465">
                    <div className="hidden lg:grid lg:grid-cols-12 gap-2 px-4 py-2.5 text-xs font-semibold text-slate-400 uppercase tracking-wider border-b border-slate-200 mb-1" data-id="element-21466">
                      <div className="col-span-5" data-id="element-21467">Астролог</div>
                      <div className="col-span-3" data-id="element-21468">Специализация</div>
                      <div className="col-span-1" data-id="element-21469">Рейтинг</div>
                      <div className="col-span-3 text-right" data-id="element-21470">Цена</div>
                    </div>
                    {Array.from({
                length: 6
              }).map((_, i) => <Fragment key={i} data-id="element-21471">
                        <SkeletonRow data-id="element-21472" />
                        <SkeletonMobileRow data-id="element-21473" />
                      </Fragment>)}
                  </div>}
              </> : currentAstrologers.length === 0 ? <div className="bg-white border border-slate-200 rounded-2xl p-12 flex flex-col items-center justify-center text-center" data-id="element-21474">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4" data-id="element-21475">
                  <SearchX className="w-8 h-8 text-slate-400" data-id="element-21476" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-21477">
                  Ничего не найдено
                </h3>
                <p className="text-slate-500 max-w-md mb-6" data-id="element-21478">
                  По вашему запросу нет астрологов. Попробуйте изменить фильтры
                  или очистить поиск.
                </p>
                <Button variant="outline" onClick={resetFilters} data-id="element-21479">
                  Сбросить все фильтры
                </Button>
              </div> : <>
                {/* ── GRID VIEW ── */}
                {viewMode === 'grid' && <motion.div key={`grid-${currentPage}`} variants={staggerContainer} initial="hidden" animate="visible" className="mb-12" data-id="element-21480">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4" data-id="element-21481">
                      {currentAstrologers.map((astrologer, idx) => {
                  const pinned = isPinnedInContext(astrologer);
                  const styles = getCardStyle(astrologer.badge, pinned);
                  const badgeLabel = getBadgeLabel(astrologer.badge);
                  return <Fragment key={astrologer.id} data-id="element-21482">
                            <motion.div variants={staggerItem} className="h-full" data-id="element-21483">
                              <div className={`relative flex flex-col h-full rounded-xl border shadow-sm hover:shadow-md transition-all p-4 ${styles.grid}`} data-id="element-21484">
                                {/* Status badges — top right corner */}
                                <div className="absolute top-2.5 right-2.5 z-10" data-id="element-21485">
                                  <BadgePill badge={astrologer.badge} pinned={pinned} data-id="element-21486" />
                                </div>

                                {/* Pinned label */}
                                {pinned && <div className="flex items-center gap-1 mb-2 -mt-1" data-id="element-21487">
                                    <Pin className="w-3 h-3 text-amber-500 rotate-45" data-id="element-21488" />
                                    <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider" data-id="element-21489">
                                      Закреплён в рубрике
                                    </span>
                                  </div>}

                                {/* Badge label (when not pinned but has badge) */}
                                {!pinned && badgeLabel && <div className="flex justify-start mb-2 -mt-1" data-id="element-21490">
                                    <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${badgeLabel.color}`} data-id="element-21491">
                                      {badgeLabel.text}
                                    </span>
                                  </div>}

                                {/* Clean avatar */}
                                <div className="relative mb-3 flex justify-center" data-id="element-21492">
                                  <div className={`w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold ${astrologer.avatarColor}`} data-id="element-21493">
                                    {getInitials(astrologer.name)}
                                  </div>
                                </div>

                                <div className="text-center mb-2" data-id="element-21494">
                                  <h3 className="text-sm font-bold text-slate-900 leading-tight" data-id="element-21495">
                                    {astrologer.name}
                                  </h3>
                                  <div className="flex justify-center mt-1" data-id="element-21496">
                                    <Rating value={astrologer.rating} count={astrologer.reviews} size="sm" data-id="element-21497" />
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-1 justify-center mb-2" data-id="element-21498">
                                  {astrologer.specialties.slice(0, 2).map(spec => <span key={spec} className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${pinned && astrologer.pinnedIn.includes(spec) ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-21499">
                                        {spec}
                                      </span>)}
                                  {astrologer.specialties.length > 2 && <span className="text-[9px] text-slate-400" data-id="element-21500">
                                      +{astrologer.specialties.length - 2}
                                    </span>}
                                </div>
                                <div className="flex justify-between items-center text-xs text-slate-500 mb-3 px-1 mt-auto" data-id="element-21501">
                                  <div className="flex items-center gap-1" data-id="element-21502">
                                    <span className="font-medium text-slate-700 text-[11px]" data-id="element-21503">
                                      {astrologer.languages.join(', ')}
                                    </span>
                                    <span className="text-[10px] text-slate-400" data-id="element-21504">
                                      · {astrologer.experience} лет
                                    </span>
                                  </div>
                                  <div className="flex gap-1.5" data-id="element-21505">
                                    {astrologer.formats.includes('video') && <Video className="w-3.5 h-3.5 text-indigo-400" title="Видеозвонок" data-id="element-21506" />}
                                    {astrologer.formats.includes('chat') && <MessageSquare className="w-3.5 h-3.5 text-emerald-400" title="Текстовый чат" data-id="element-21507" />}
                                  </div>
                                </div>
                                <div className={`pt-3 border-t ${styles.divider}`} data-id="element-21508">
                                  <div className="flex items-center justify-between mb-2" data-id="element-21509">
                                    <span className="text-[11px] text-slate-500" data-id="element-21510">
                                      Консультация от
                                    </span>
                                    <span className="text-sm font-bold text-slate-900" data-id="element-21511">
                                      {astrologer.price.toLocaleString('ru-RU')}{' '}
                                      ₽
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5" data-id="element-21512">
                                    <button onClick={e => toggleFavorite(e, astrologer.id)} className={`flex-shrink-0 p-1.5 rounded-lg border transition-all ${favorites.has(astrologer.id) ? 'border-rose-200 bg-rose-50 hover:bg-rose-100' : 'border-slate-200 bg-white hover:border-rose-200 hover:bg-rose-50'}`} title={favorites.has(astrologer.id) ? 'Убрать из избранного' : 'В избранное'} data-id="element-21513">
                                      <Heart className={`w-4 h-4 transition-colors ${favorites.has(astrologer.id) ? 'text-rose-500 fill-rose-500' : 'text-slate-400 hover:text-rose-400'}`} data-id="element-21514" />
                                    </button>
                                    <div className="grid grid-cols-2 gap-1.5 flex-1" data-id="element-21515">
                                      <Button variant="outline" size="sm" onClick={() => onNavigate('astrologer-profile')} className="text-[11px]" data-id="element-21516">
                                        Профиль
                                      </Button>
                                      <Button variant="primary" size="sm" onClick={() => {
                                addItem({
                                  id: `consultation-${astrologer.id}-${Date.now()}`,
                                  name: `Консультация — ${astrologer.name}`,
                                  description: `Онлайн-консультация с астрологом ${astrologer.name}`,
                                  price: astrologer.price,
                                  type: 'consultation',
                                  format: astrologer.formats.includes('video') ? 'Видеозвонок' : 'Чат',
                                  badges: [{
                                    label: 'Консультация',
                                    variant: 'pro'
                                  }]
                                });
                                onNavigate('checkout');
                              }} className="text-[11px]" data-id="element-21517">
                                        Заказать
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </motion.div>

                            {(idx + 1) % 6 === 0 && idx < currentAstrologers.length - 1 && <motion.div variants={staggerItem} className="col-span-1 sm:col-span-2 lg:col-span-3 2xl:col-span-4" data-id="element-21518">
                                  <AdSlot slotId="SLOT-20" onNavigate={onNavigate} data-id="element-21519" />
                                </motion.div>}
                          </Fragment>;
                })}
                    </div>
                  </motion.div>}

                {/* ── LIST / TABLE VIEW ── */}
                {viewMode === 'list' && <motion.div key={`list-${currentPage}`} variants={staggerContainer} initial="hidden" animate="visible" className="mb-12" data-id="element-21520">
                    {/* Table header — only on lg+ where sidebar coexists */}
                    <div className="hidden lg:grid lg:grid-cols-12 gap-2 px-4 py-2.5 text-xs font-semibold text-slate-400 uppercase tracking-wider border-b border-slate-200 mb-1" data-id="element-21521">
                      <div className="col-span-5" data-id="element-21522">Астролог</div>
                      <div className="col-span-3" data-id="element-21523">Специализация</div>
                      <div className="col-span-1" data-id="element-21524">Рейтинг</div>
                      <div className="col-span-3 text-right" data-id="element-21525">Цена</div>
                    </div>

                    {currentAstrologers.map((astrologer, idx) => {
                const pinned = isPinnedInContext(astrologer);
                const styles = getCardStyle(astrologer.badge, pinned);
                return <Fragment key={astrologer.id} data-id="element-21526">
                          <motion.div variants={staggerItem} data-id="element-21527">
                            {/* Desktop row — lg+ */}
                            <div className={`hidden lg:grid lg:grid-cols-12 gap-2 items-center px-4 py-3 border rounded-lg mb-2 hover:shadow-md transition-all cursor-pointer group ${astrologer.badge && !pinned ? `border-l-[3px] ${astrologer.badge === 'TOP' ? 'border-l-amber-400' : astrologer.badge === 'PRO' ? 'border-l-indigo-400' : 'border-l-emerald-400'} ` : ''}${styles.list}`} onClick={() => onNavigate('astrologer-profile')} data-id="element-21528">
                              <div className="col-span-5 flex items-center gap-2.5 min-w-0" data-id="element-21529">
                                <button onClick={e => toggleFavorite(e, astrologer.id)} className="flex-shrink-0 p-0.5 rounded-full hover:bg-slate-100 transition-colors" title={favorites.has(astrologer.id) ? 'Убрать из избранного' : 'В избранное'} data-id="element-21530">
                                  <Heart className={`w-3.5 h-3.5 transition-colors ${favorites.has(astrologer.id) ? 'text-rose-500 fill-rose-500' : 'text-slate-300 hover:text-rose-400'}`} data-id="element-21531" />
                                </button>
                                <div className="relative flex-shrink-0" data-id="element-21532">
                                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${astrologer.avatarColor}`} data-id="element-21533">
                                    {getInitials(astrologer.name)}
                                  </div>
                                </div>
                                <div className="min-w-0" data-id="element-21534">
                                  <div className="flex items-center gap-1.5" data-id="element-21535">
                                    <span className="text-sm font-bold text-slate-900 truncate group-hover:text-indigo-600 transition-colors" data-id="element-21536">
                                      {astrologer.name}
                                    </span>
                                    <InlineBadges badge={astrologer.badge} pinned={pinned} data-id="element-21537" />
                                  </div>
                                  <div className="flex items-center gap-1.5 text-[11px] text-slate-500" data-id="element-21538">
                                    <span data-id="element-21539">
                                      {astrologer.languages.join(', ')}
                                    </span>
                                    <span data-id="element-21540">·</span>
                                    <span data-id="element-21541">{astrologer.experience} лет</span>
                                    <span data-id="element-21542">·</span>
                                    <span className="flex items-center gap-0.5" data-id="element-21543">
                                      {astrologer.formats.includes('video') && <Video className="w-3 h-3 text-indigo-400" data-id="element-21544" />}
                                      {astrologer.formats.includes('chat') && <MessageSquare className="w-3 h-3 text-emerald-400" data-id="element-21545" />}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="col-span-3 flex flex-wrap gap-1" data-id="element-21546">
                                {astrologer.specialties.slice(0, 2).map(spec => <span key={spec} className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${pinned && astrologer.pinnedIn.includes(spec) ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-21547">
                                      {spec}
                                    </span>)}
                                {astrologer.specialties.length > 2 && <span className="text-[10px] text-slate-400" data-id="element-21548">
                                    +{astrologer.specialties.length - 2}
                                  </span>}
                              </div>
                              <div className="col-span-1 flex items-center gap-1" data-id="element-21549">
                                <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" data-id="element-21550" />
                                <span className="text-sm font-medium text-slate-700" data-id="element-21551">
                                  {astrologer.rating}
                                </span>
                              </div>
                              <div className="col-span-3 flex items-center justify-end gap-2" data-id="element-21552">
                                <span className="text-sm font-bold text-slate-900 whitespace-nowrap" data-id="element-21553">
                                  {astrologer.price.toLocaleString('ru-RU')} ₽
                                </span>
                                <Button variant="primary" size="sm" onClick={e => {
                          e.stopPropagation();
                          addItem({
                            id: `consultation-${astrologer.id}-${Date.now()}`,
                            name: `Консультация — ${astrologer.name}`,
                            description: `Онлайн-консультация с астрологом ${astrologer.name}`,
                            price: astrologer.price,
                            type: 'consultation',
                            format: astrologer.formats.includes('video') ? 'Видеозвонок' : 'Чат',
                            badges: [{
                              label: 'Консультация',
                              variant: 'pro'
                            }]
                          });
                          onNavigate('checkout');
                        }} className="text-xs whitespace-nowrap" data-id="element-21554">
                                  Заказать
                                </Button>
                              </div>
                            </div>

                            {/* Mobile + tablet row — below lg */}
                            <div className={`lg:hidden flex items-start gap-2.5 px-3 py-3 border rounded-lg mb-2 hover:shadow-sm transition-shadow cursor-pointer ${astrologer.badge && !pinned ? `border-l-[3px] ${astrologer.badge === 'TOP' ? 'border-l-amber-400' : astrologer.badge === 'PRO' ? 'border-l-indigo-400' : 'border-l-emerald-400'} ` : ''}${pinned ? 'border-l-[3px] border-l-amber-400 ' : ''}${styles.mobile}`} onClick={() => onNavigate('astrologer-profile')} data-id="element-21555">
                              <div className="relative flex-shrink-0 mt-0.5" data-id="element-21556">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${astrologer.avatarColor}`} data-id="element-21557">
                                  {getInitials(astrologer.name)}
                                </div>
                                {/* Favorite — bottom right of avatar on mobile */}
                                <button onClick={e => toggleFavorite(e, astrologer.id)} className="absolute -bottom-1 -right-1 p-0.5 rounded-full bg-white shadow-sm border border-slate-100" title={favorites.has(astrologer.id) ? 'Убрать из избранного' : 'В избранное'} data-id="element-21558">
                                  <Heart className={`w-3 h-3 transition-colors ${favorites.has(astrologer.id) ? 'text-rose-500 fill-rose-500' : 'text-slate-300'}`} data-id="element-21559" />
                                </button>
                              </div>
                              <div className="flex-grow min-w-0" data-id="element-21560">
                                <div className="flex items-center gap-1" data-id="element-21561">
                                  <span className="text-[13px] font-bold text-slate-900 truncate" data-id="element-21562">
                                    {astrologer.name}
                                  </span>
                                  <InlineBadges badge={astrologer.badge} pinned={pinned} data-id="element-21563" />
                                </div>
                                <div className="text-[11px] text-slate-500 truncate mt-0.5" data-id="element-21564">
                                  {astrologer.specialties.slice(0, 2).join(' · ')}
                                </div>
                                <div className="flex items-center gap-1.5 mt-1" data-id="element-21565">
                                  <div className="flex items-center gap-0.5" data-id="element-21566">
                                    <Star className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-21567" />
                                    <span className="text-[11px] font-medium text-slate-600" data-id="element-21568">
                                      {astrologer.rating}
                                    </span>
                                  </div>
                                  <span className="text-[10px] text-slate-400" data-id="element-21569">
                                    ({astrologer.reviews})
                                  </span>
                                  <span className="text-[10px] text-slate-400" data-id="element-21570">
                                    · {astrologer.experience} лет
                                  </span>
                                  <span className="flex items-center gap-0.5 ml-auto" data-id="element-21571">
                                    {astrologer.formats.includes('video') && <Video className="w-3 h-3 text-indigo-400" data-id="element-21572" />}
                                    {astrologer.formats.includes('chat') && <MessageSquare className="w-3 h-3 text-emerald-400" data-id="element-21573" />}
                                  </span>
                                </div>
                              </div>
                              <div className="flex flex-col items-end flex-shrink-0 gap-1" data-id="element-21574">
                                <span className="text-[13px] font-bold text-slate-900" data-id="element-21575">
                                  {astrologer.price.toLocaleString('ru-RU')} ₽
                                </span>
                                <Button variant="primary" size="sm" onClick={e => {
                          e.stopPropagation();
                          addItem({
                            id: `consultation-${astrologer.id}-${Date.now()}`,
                            name: `Консультация — ${astrologer.name}`,
                            description: `Онлайн-консультация с астрологом ${astrologer.name}`,
                            price: astrologer.price,
                            type: 'consultation',
                            format: astrologer.formats.includes('video') ? 'Видеозвонок' : 'Чат',
                            badges: [{
                              label: 'Консультация',
                              variant: 'pro'
                            }]
                          });
                          onNavigate('checkout');
                        }} className="text-[10px] px-2.5 py-1" data-id="element-21576">
                                  Заказать
                                </Button>
                              </div>
                            </div>
                          </motion.div>

                          {(idx + 1) % 6 === 0 && idx < currentAstrologers.length - 1 && <motion.div variants={staggerItem} className="mb-2" data-id="element-21577">
                                <AdSlot slotId="SLOT-20" onNavigate={onNavigate} data-id="element-21578" />
                              </motion.div>}
                        </Fragment>;
              })}
                  </motion.div>}

                {totalPages > 1 && <div className="flex justify-center" data-id="element-21579">
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-21580" />
                  </div>}
              </>}
          </div>
        </div>
      </div>
    </div>;
}